import {
  ButtonType,
  ButtonWidget,
  CheckBoxWithLabelWidget,
  DialogPositioningBehavior,
  DialogType,
  DropdownButtonType,
  InputLabelWidget,
  List,
  ListItem,
  SingleSelectDropdown,
  TextFieldWidget,
  TextLinkWidget
} from '@tableau/widgets';
import {FormikProps, withFormik} from 'formik';
import React from 'react';

import {Messages} from '../../codegen/Localize';
import {imgUrl} from '../../common/utilities/ImgUrl';
import {openReactConfirmationDialog} from '../../commonInfra/dialogs/ReactConfirmationDialog';
import {ModalInstance, ReactModalService} from '../../commonInfra/dialogs/ReactModalService';
import {TabDialogWidth} from '../../commonInfra/dialogs/style/TabDialogWidth';
import {IResult} from '../../httpClient/serverApi/types/IResponse';
import {FeatureFlags} from '../../ts/FeatureFlags';
import {IEditableServerSettings} from '../../ts/ServerApi/types/IEditableServerSettings';
import {
  AnalyticsExtensionsSettingsCtrlProps,
  IAnalyticsExtensionsSettings
} from '../controllers/SiteSettingsForAnalyticsExtensionsCtrl';
import {ServerSettingsInstance} from '../services/ServerSettings';
import {AnalyticsExtensionsSettingsStyles} from '../styles/AnalyticsExtensionsSettingsStyles';
import {
  IAnalyticsExtConnectionId,
  IAnalyticsExtConnectionSettings,
  IAnalyticsExtSiteSettings
} from '../types/ISiteSettingsForSiteAdmin';
import {AnalyticsExtensionsSettingsDialog} from './AnalyticsExtensionsSettingsDialog';

export interface AnalyticsExtensionsSettingsForSiteValues {
  showAnalyticsSiteSetting: boolean;
  showSiteCheckbox: boolean;
  enableExternalServicesCheckbox: boolean;
  connectionLUID: string;
  connectionName: string;
  connectionType: string;
  hostName: string;
  port?: number;
  isAuth?: boolean;
  userName?: string;
  password?: string;
  isSecure?: boolean;
  namedConnectionList?: IAnalyticsExtConnectionSettings[];
}

export const ConnectionTypes = {
  TABPY: {
    TAG: 'TABPY',
    DISPLAY: 'TabPy/Analytics Extension'
  },
  RSERVE: {
    TAG: 'RSERVE',
    DISPLAY: 'Rserve'
  },
  EINSTEIN: {
    TAG: 'EINSTEIN_DISCOVERY',
    DISPLAY: 'Einstein Discovery'
  },
  GENERIC_API: {
    TAG: 'GENERIC_API',
    DISPLAY: 'Analytics Extension API'
  },
  SELECT: 'Select'
};


const connectionItems: ListItem[] = [
  {value: ConnectionTypes.SELECT},
  {value: ConnectionTypes.TABPY.DISPLAY},
  {value: ConnectionTypes.RSERVE.DISPLAY}
];

const MAX_HOSTNAME_LENGTH: number = 255;
const MIN_PORT_NUMBER: number = 0;
const MAX_PORT_NUMBER: number = 65535;
const MAX_USERNAME_LENGTH: number = 255;
const MAX_PASSWORD_LENGTH: number = 255;

const EINSTEIN_DISCOVERY_CELL_ICON = imgUrl('einstein_discovery_new_connection.svg');
const TABPY_CELL_ICON = imgUrl('tabpy_new_connection.svg');
const RSERVE_CELL_ICON = imgUrl('rserve_new_connection.svg');
const ANALYTICS_EXTENSION_API_CELL_ICON = imgUrl('analytics_extensions_api.svg');
const NO_CONNECTION_ICON = imgUrl('extension.svg');

export type InnerAnalyticsExtensionsSettingForSiteProps =
  AnalyticsExtensionsSettingsCtrlProps
  & FormikProps<AnalyticsExtensionsSettingsForSiteValues>
  & AnalyticsExtensionsSettingsForSiteValues;

export function AnExtSiteSettingRemoveActionStringBody(): JSX.Element {
  return (
    <div>
      <h3 className={AnalyticsExtensionsSettingsStyles.connectionDeleteConfirmationDialogHeader}>
        {Messages.analyticsExtensionsRemoveConnectionHeader()}
      </h3>
      <div className={AnalyticsExtensionsSettingsStyles.connectionDeleteConfirmationDialogDescription}>
        {Messages.analyticsExtensionsRemoveConnectionText()}
      </div>
    </div>
  );
}

export function AnExtSiteSettingRemoveActionStringBodyEDFFOn(): JSX.Element {
  return (
    <div>
      <div className={AnalyticsExtensionsSettingsStyles.connectionDeleteConfirmationDialogHeaderContainerEDFFOn}>
        <div className={AnalyticsExtensionsSettingsStyles.connectionDeleteConfirmationDialogHeaderEDFFOn}>
          {Messages.analyticsExtensionsRemoveConnectionHeaderEDFFOn({CONNECTION_TYPE: 'Connection'})}
        </div>
      </div>
      <div className={AnalyticsExtensionsSettingsStyles.connectionDeleteConfirmationDialogDescriptionEDFFOn}
           style={{fontSize: '16px'}}>
        {Messages.analyticsExtensionsRemoveConnectionTextHeader()}
      </div>
      <div className={AnalyticsExtensionsSettingsStyles.connectionDeleteConfirmationDialogDescriptionEDFFOn}
           style={{fontSize: '12px'}}>
        {Messages.analyticsExtensionsRemoveConnectionTextEDFFOn()}
      </div>
    </div>
  );
}

export function confirmDeleteConnectionDialog(luid: string, request: (connectionId: IAnalyticsExtConnectionId) => Promise<IResult>) {
  const dialogInstance = openReactConfirmationDialog<void, IResult>({
    bodyComponent: FeatureFlags.einsteinDiscoveryPredictionsAnalyticsExtension ? AnExtSiteSettingRemoveActionStringBodyEDFFOn : AnExtSiteSettingRemoveActionStringBody,
    message: '',
    paramsForAction: undefined,
    actionName: FeatureFlags.einsteinDiscoveryPredictionsAnalyticsExtension ? Messages.analyticsExtensionsRemoveConnectionActionNameEDFFOn() : Messages.analyticsExtensionsRemoveConnectionActionName(),
    action: () => request({connection_luid: luid}),
    isActionDelete: true,
    dialogWidth: TabDialogWidth.width375px
  });
  return dialogInstance.result;
}

export class InnerAnalyticsExtensionsSettingForSite extends React.PureComponent <InnerAnalyticsExtensionsSettingForSiteProps> {
  private serverSettings: IEditableServerSettings;

  constructor(props: InnerAnalyticsExtensionsSettingForSiteProps) {
    super(props);
    props.setSaveCallback(() => this.sendUpdateSave());

    if (FeatureFlags.anExtRedesignV20203Enabled) {
      props.setCancelCallback(() => {
        this.handleChangeAdapter(this.props.initialValues.enableExternalServicesCheckbox, 'enableExternalServicesCheckbox');
        this.props.setIsFormDirty(false);
      });
    } else {
      props.setCancelCallback(props.handleReset);
    }

    props.setIsBusyCallback(() => props.isSubmitting);
    if (FeatureFlags.anExtRedesignV20203Enabled) {
      this.sendDelete = this.sendDelete.bind(this);
    }

    this.getServerSettings();
  }

  public sendUpdateSave(): Promise<IResult> {
    // need showAnalyticsSiteSetting check because current design require the hack to handle signle/multisite
    // this is how extension team does it, refactoring requires lots of work and would need to refactor their page
    if (this.props.values.showAnalyticsSiteSetting) {
      const updateSiteSettings: IAnalyticsExtSiteSettings = {enabled: this.props.values.enableExternalServicesCheckbox};
      if (FeatureFlags.anExtRedesignV20203Enabled) {
        this.props.handleSubmit();
        return this.props.sendUpdateRequest(updateSiteSettings, null);
      }

      let connection = '';
      if (this.props.values.connectionType === ConnectionTypes.TABPY.DISPLAY) {
        connection = ConnectionTypes.TABPY.TAG;
      } else if (this.props.values.connectionType === ConnectionTypes.RSERVE.DISPLAY) {
        connection = ConnectionTypes.RSERVE.TAG;
      } else if (this.props.values.connectionType === ConnectionTypes.EINSTEIN.DISPLAY) {
        connection = ConnectionTypes.EINSTEIN.TAG;
      } else if (this.props.values.connectionType === ConnectionTypes.GENERIC_API.DISPLAY) {
        connection = ConnectionTypes.GENERIC_API.TAG;
      } else { // No connection created. Only save the site enable/disbale setting
        this.props.handleSubmit();
        return this.props.sendUpdateRequest(updateSiteSettings, null);
      }
      const updateConnectionSettings: IAnalyticsExtConnectionSettings = {
        connection_brief: {
          connection_type: connection,
          connection_name: this.props.values.connectionName
        },
        connection_luid: this.props.values.connectionLUID,
        host: this.props.values.hostName,
        port: this.props.values.port,
        is_ssl_enabled: this.props.values.isSecure,
        is_auth_enabled: this.props.values.isAuth,
        username: this.props.values.userName,
        password: this.props.values.password,
      };
      this.props.handleSubmit();
      return this.props.sendUpdateRequest(updateSiteSettings, updateConnectionSettings);
    }
    this.props.handleSubmit();
  }

  public componentDidUpdate() {
    // Let the Angular controller know things have changed
    this.props.setIsFormDirty(this.isDirty());
    this.props.setIsFormValid(this.props.isValid);
    this.props.setIsBusyCallback(() => this.props.isSubmitting);
  }

  private handleChangeAdaptorMultipleConnections = (updatedConnection: IAnalyticsExtConnectionSettings) => {
    let connectionList = Object.assign([], this.props.values.namedConnectionList);
    let found = false;
    connectionList.forEach((connection: IAnalyticsExtConnectionSettings, index: number) => {
      if (connection.connection_luid === updatedConnection.connection_luid || connection.connection_luid === '') {
        found = true;
        connectionList[index] = updatedConnection;
      }
    });
    if (!found) {
      connectionList.push(updatedConnection);
    }
    this.handleChangeAdapter(connectionList, 'namedConnectionList');
  }

  private handleChangeAdapter = (value: any, fieldName: keyof AnalyticsExtensionsSettingsForSiteValues) => {
    const event = {
      target: {
        name: fieldName,
        value
      }
    };
    // Call the Formik handleChange
    this.props.handleChange(event);
  }

  private clearLoginInfo() {
    this.handleChangeAdapter('', 'userName');
    this.handleChangeAdapter('', 'password');
  }

  private showConnectionDialog(ctrlProps: AnalyticsExtensionsSettingsCtrlProps, connectionLUID: string) {
    let modalInstance: ModalInstance;
    const dialogProps = {
      dismiss: () => modalInstance && modalInstance.dismiss(),
      close: (result: any) => modalInstance && modalInstance.close(result),
      connectionLUID,
      ctrlProps: ctrlProps
    };

    modalInstance = ReactModalService.open({
      type: DialogType.Uber,
      dialogContent: React.createElement(AnalyticsExtensionsSettingsDialog, dialogProps),
      testId: 'analyticsConnectionsSettingDialog',
      showCloseButton: true,
      //dialogStyle: serverAndSiteUsersDialogStyle,
      shouldNotSetDefaultWidth: true
    });
    return modalInstance.result;
  }

  private showHybridDialog(ctrlProps: AnalyticsExtensionsSettingsCtrlProps, connectionLUID: string) {
    let modalInstance: ModalInstance;
    const dialogProps = {
      dismiss: () => modalInstance && modalInstance.dismiss(),
      close: (result: any) => modalInstance && modalInstance.close(result),
      connectionLUID,
      ctrlProps: ctrlProps,
      // if serverSettings is undefined, we are in online which means savedAccessTokensEnabled should be true
      savedAccessTokensEnabled: this.serverSettings ? this.serverSettings.savedAccessTokensEnabled : true
    };

    modalInstance = ReactModalService.open({
      type: DialogType.NormalWithTitleBar,
      title: this.isCreating(connectionLUID) ? Messages.analyticsExtensionsSettingsCreateConnectionDialogHeader() : Messages.analyticsExtensionsSettingsEditConnectionDialogHeader(),
      dialogContent: React.createElement(AnalyticsExtensionsSettingsDialog, dialogProps),
      testId: 'analyticsConnectionsSettingDialog',
      showCloseButton: true,
      //dialogStyle: serverAndSiteUsersDialogStyle,
      shouldNotSetDefaultWidth: true,
      positioningBehavior: DialogPositioningBehavior.Draggable,
      shouldNotCloseOnGlassClick: true
    });
    return modalInstance.result;
  }

  private isCreating(connectionLUID: string) {
    if (!FeatureFlags.analyticsExtensionsNamedConnections) {
      const params = this.props.values;
      return params === undefined || params === null || params.connectionType === ConnectionTypes.SELECT;
    } else {
      return connectionLUID === '';
    }
  }

  private updateConnectionInfo(result: IAnalyticsExtConnectionSettings) {
    if (result) {
      const updateConnection = result;
      let connectionDisplay = '';
      if (updateConnection.connection_brief.connection_type === ConnectionTypes.TABPY.TAG) {
        connectionDisplay = ConnectionTypes.TABPY.DISPLAY;
      }
      if (updateConnection.connection_brief.connection_type === ConnectionTypes.RSERVE.TAG) {
        connectionDisplay = ConnectionTypes.RSERVE.DISPLAY;
      }
      if (updateConnection.connection_brief.connection_type === ConnectionTypes.EINSTEIN.TAG) {
        connectionDisplay = ConnectionTypes.EINSTEIN.DISPLAY;
      }
      if (updateConnection.connection_brief.connection_type === ConnectionTypes.GENERIC_API.TAG) {
        connectionDisplay = ConnectionTypes.GENERIC_API.DISPLAY;
      }
      if (!FeatureFlags.analyticsExtensionsNamedConnections) {
        this.handleChangeAdapter(connectionDisplay, 'connectionType');
        this.handleChangeAdapter(updateConnection.connection_luid, 'connectionLUID');
        this.handleChangeAdapter(updateConnection.host, 'hostName');
      } else {
        updateConnection.connection_brief.connection_type = connectionDisplay;
        this.handleChangeAdaptorMultipleConnections(updateConnection);
      }
      this.props.setIsFormDirty(this.isDirty());
    }
  }

  private sendDelete(connectionLUID: string) {
    let result = confirmDeleteConnectionDialog(connectionLUID, this.props.sendDeleteRequest);

    // After I increased the type strictness on ReactModalService, the following result type gave an error.
    // The typedefs say that `sendDeleteRequest` returns an `IResult`, but it looks like the API
    // might actually return `IAnalyticsExtensionsSettings[]`. Since the error is many levels deep
    // and I don't own this file, I'm adding this type assertion and leaving this comment. (abrandon 2020-08-11)
    let resultOfQuestionableType = result as unknown as PromiseLike<IAnalyticsExtensionsSettings[]>;
    resultOfQuestionableType.then((result: IAnalyticsExtensionsSettings[]) => {
      if (!FeatureFlags.analyticsExtensionsNamedConnections) {
        if (!result.length) {
          this.props.initialAnalyticsExtensionsSettings.connectionDefault = null;
          this.handleChangeAdapter(ConnectionTypes.SELECT, 'connectionType');
        }
      } else {
        //If named connections ff is on, we need to remove this connection from our named connections
        let initialConnectionList = this.props.initialAnalyticsExtensionsSettings.connectionSettingsList;
        initialConnectionList.forEach((connection, index) => {
          if (connection.connection_luid === connectionLUID) {
            this.props.initialAnalyticsExtensionsSettings.connectionSettingsList.splice(index, 1);
          }
        });
        let namedConnections = Object.assign([], this.props.values.namedConnectionList);
        namedConnections.forEach((connection, index) => {
          if (connection.connection_luid === connectionLUID) {
            namedConnections.splice(index, 1);
          }
        });
        if (namedConnections.length === 0) {
          let brief = defaultConnection.connection_brief;
          brief = {
            ...brief,
            connection_type: ConnectionTypes.SELECT
          };
          namedConnections.push({
            ...defaultConnection,
            connection_brief: brief
          });
        }
        this.handleChangeAdapter(namedConnections, 'namedConnectionList');
      }
      this.props.setIsFormDirty(this.isDirty());
    });
  }

  private isDirty() {
    let dirty = this.props.dirty;
    if (FeatureFlags.anExtRedesignV20203Enabled) {
      dirty = this.props.values.enableExternalServicesCheckbox !== this.props.initialValues.enableExternalServicesCheckbox;
    }
    return dirty;
  }

  private getConnectionTypeIcon(type: String) {
    switch (type) {
      case ConnectionTypes.RSERVE.DISPLAY: {
        return RSERVE_CELL_ICON;
      }
      case ConnectionTypes.TABPY.DISPLAY: {
        return TABPY_CELL_ICON;
      }
      case ConnectionTypes.EINSTEIN.DISPLAY: {
        return EINSTEIN_DISCOVERY_CELL_ICON;
      }
      case ConnectionTypes.GENERIC_API.DISPLAY: {
        return ANALYTICS_EXTENSION_API_CELL_ICON;
      }
      default: {
        return NO_CONNECTION_ICON;
      }
    }
  }

  private getServerSettings() {
    ServerSettingsInstance.fetch().then((settings) => {
      this.serverSettings = settings;
    }).catch(() => {
      this.serverSettings = { savedAccessTokensEnabled: false };
    });
  }

  render() {
    if (!this.props.values.showAnalyticsSiteSetting) {
      return (
        <section/>
      );
    }

    let extensionsDisplay;

    if (!FeatureFlags.anExtRedesignV20203Enabled) {
      extensionsDisplay =
        <div
          className={!FeatureFlags.anExtRedesignV20203Enabled ? AnalyticsExtensionsSettingsStyles.connectionSettingsTable : AnalyticsExtensionsSettingsStyles.Hide}>
          <div className={AnalyticsExtensionsSettingsStyles.connectionSettingsRow}>
            <div className={AnalyticsExtensionsSettingsStyles.connectionSettingsCell}>
              <SingleSelectDropdown
                testId="anext_site_connection_dropdown"
                label={Messages.analyticsExtensionsSettingsConnectoinType()}
                list={<List items={connectionItems} selectedValue={this.props.values.connectionType}/>}
                onChange={(connectionType: string) => {
                  this.handleChangeAdapter(connectionType, 'connectionType');
                }
                }
                onBlur={e => {
                  this.props.handleBlur(e);
                }}
                style={{width: 145}}
                type={DropdownButtonType.Line}
                disabled={false}
                value={this.props.values.connectionType}
                valid={this.props.errors.connectionType ? false : undefined}
              />
            </div>
            <div className={AnalyticsExtensionsSettingsStyles.connectionSettingsCell}>
              <TextFieldWidget
                testId="anext_site_host_textfield"
                label={Messages.host()}
                onChange={e => {
                  this.handleChangeAdapter(e.target.value, 'hostName');
                  this.clearLoginInfo();
                }
                }
                onBlur={e => {
                  this.props.handleBlur(e);
                }}
                value={this.props.values.hostName}
                disabled={this.props.values.connectionType === ConnectionTypes.SELECT}
                valid={this.props.errors.hostName ? false : undefined}
              />
            </div>
            <div className={AnalyticsExtensionsSettingsStyles.connectionSettingsCell}>
              <TextFieldWidget
                testId="anext_site_port_textfield"
                label={Messages.port()}
                onChange={e => {
                  this.handleChangeAdapter(e.target.value, 'port');
                  this.clearLoginInfo();
                }
                }
                onBlur={e => {
                  this.props.handleBlur(e);
                }}
                value={this.props.values.port ?? ''}
                className={AnalyticsExtensionsSettingsStyles.connectionPortInput}
                disabled={this.props.values.connectionType === ConnectionTypes.SELECT}
                valid={this.props.errors.port ? false : undefined}
              />
            </div>
            <div className={AnalyticsExtensionsSettingsStyles.connectionSettingsCell}>
              <CheckBoxWithLabelWidget
                checked={this.props.values.isSecure}
                handleChange={(isBoxChecked: boolean) => {
                  this.handleChangeAdapter(isBoxChecked, 'isSecure');
                  this.clearLoginInfo();
                }}
                testId="anext_site_secure_checkbox"
                label={Messages.ssl()}
                containerStyle={AnalyticsExtensionsSettingsStyles.connectionSslCheckbox}
                disabled={this.props.values.connectionType === ConnectionTypes.SELECT}
              />
            </div>
            <div className={AnalyticsExtensionsSettingsStyles.connectionSettingsCell}>
              <CheckBoxWithLabelWidget
                checked={this.props.values.isAuth}
                handleChange={(isBoxChecked: boolean) => {
                  this.handleChangeAdapter(isBoxChecked, 'isAuth');
                  this.clearLoginInfo();
                }}
                testId="anext_site_auth_checkbox"
                label={Messages.authentication()}
                disabled={this.props.values.connectionType === ConnectionTypes.SELECT}
              />
            </div>
            <div className={AnalyticsExtensionsSettingsStyles.connectionSettingsCell}>
              <TextFieldWidget
                testId="anext_site_userName_textfield"
                label={Messages.username()}
                onChange={e => {
                  this.handleChangeAdapter(e.target.value, 'userName');
                }}
                onBlur={e => {
                  this.props.handleBlur(e);
                }}
                value={this.props.values.userName}
                disabled={(this.props.values.connectionType === ConnectionTypes.SELECT) || (!this.props.values.isAuth)}
                valid={this.props.errors.userName ? false : undefined}
              />
            </div>
            <div className={AnalyticsExtensionsSettingsStyles.connectionSettingsCell}>
              <TextFieldWidget
                testId="anext_site_password_textfield"
                label={Messages.password()}
                onChange={e => {
                  this.handleChangeAdapter(e.target.value, 'password');
                }}
                onBlur={e => {
                  this.props.handleBlur(e);
                }}
                value={this.props.values.password}
                disabled={(this.props.values.connectionType === ConnectionTypes.SELECT) || (!this.props.values.isAuth)}
                type={'password'}
                valid={this.props.errors.password ? false : undefined}
              />
            </div>
          </div>
        </div>;
    } else if (!FeatureFlags.analyticsExtensionsNamedConnections && !FeatureFlags.einsteinDiscoveryPredictionsAnalyticsExtension) {
      extensionsDisplay =
        <div>
          <div
            className={(FeatureFlags.anExtRedesignV20203Enabled && !FeatureFlags.einsteinDiscoveryPredictionsAnalyticsExtension) ? AnalyticsExtensionsSettingsStyles.connectionSettingsTable : AnalyticsExtensionsSettingsStyles.Hide}>
            <div className={AnalyticsExtensionsSettingsStyles.connectionRow}>
              <div className={AnalyticsExtensionsSettingsStyles.connectionHeaderCell} style={{width: '40%'}}>
                <InputLabelWidget testId="anext_connection_type_title" style={{fontWeight: 'bold'}}>
                  {Messages.analyticsExtensionsSettingsConnectionType()}
                </InputLabelWidget>
              </div>
              <div className={AnalyticsExtensionsSettingsStyles.connectionHeaderCell} style={{width: '60%'}}>
                <InputLabelWidget testId="anext_hostname_title" style={{fontWeight: 'bold'}}>
                  {Messages.analyticsExtensionsSettingsHostname()}
                </InputLabelWidget>
              </div>
            </div>
          </div>
          <div
            className={(FeatureFlags.anExtRedesignV20203Enabled && !FeatureFlags.einsteinDiscoveryPredictionsAnalyticsExtension) ? AnalyticsExtensionsSettingsStyles.connectionSettingsTable : AnalyticsExtensionsSettingsStyles.Hide}>
            <div
              className={this.props.values.connectionType === ConnectionTypes.SELECT ? AnalyticsExtensionsSettingsStyles.connectionRow : AnalyticsExtensionsSettingsStyles.Hide}>
              <div className={AnalyticsExtensionsSettingsStyles.emptyConnectionPrompt}>
                <InputLabelWidget testId="anext_no_connection_label">
                  {Messages.analyticsExtensionsNoConnectionConfigured()}
                </InputLabelWidget>
                <TextLinkWidget testId="anext_connection_create_textlink" onClick={() => {
                  this.showConnectionDialog(this.props, this.props.values.connectionLUID).then(
                    (result: IAnalyticsExtConnectionSettings) => {
                      this.updateConnectionInfo(result);
                    }
                  );
                }}>
                  <u style={{fontStyle: 'normal', fontWeight: 'normal', paddingLeft: '5px', paddingRight: '5px'}}>
                    {Messages.analyticsExtensionsSettingsCreateConnection()}
                  </u>
                </TextLinkWidget>
              </div>
            </div>
            <div
              className={this.props.values.connectionType === ConnectionTypes.SELECT ? AnalyticsExtensionsSettingsStyles.Hide : AnalyticsExtensionsSettingsStyles.connectionRow}>
              <div className={AnalyticsExtensionsSettingsStyles.connectionCell} style={{width: '40%'}}>
                <InputLabelWidget testId="anext_connection_type_value">
                  {this.props.values.connectionType}
                </InputLabelWidget>
              </div>
              <div className={AnalyticsExtensionsSettingsStyles.connectionCell} style={{width: '40%'}}>
                <InputLabelWidget testId="anext_hostname_value">
                  {this.props.values.hostName}
                </InputLabelWidget>
              </div>
              <div className={AnalyticsExtensionsSettingsStyles.connectionCell} style={{width: '5%'}}>
                <TextLinkWidget testId="anext_connection_edit_textlink" onClick={() => {
                  this.showConnectionDialog(this.props, this.props.values.connectionLUID).then(
                    (result: IAnalyticsExtConnectionSettings) => {
                      this.updateConnectionInfo(result);
                    }
                  );
                }}>
                  <u style={{fontWeight: 'normal'}}>
                    {Messages.analyticsExtensionsSettingsEdit()}
                  </u>
                </TextLinkWidget>
              </div>
              <div className={AnalyticsExtensionsSettingsStyles.connectionCell} style={{width: '5%'}}>
                <TextLinkWidget testId="anext_connection_delete_textlink"
                                onClick={() => this.sendDelete(this.props.connectionLUID)}>
                  <u style={{fontWeight: 'normal'}}>
                    {Messages.analyticsExtensionsSettingsDelete()}
                  </u>
                </TextLinkWidget>
              </div>
            </div>
          </div>
        </div>;
    } else if (!FeatureFlags.analyticsExtensionsNamedConnections && FeatureFlags.einsteinDiscoveryPredictionsAnalyticsExtension) {
      extensionsDisplay =
        <div>
          <div
            className={AnalyticsExtensionsSettingsStyles.connectionSettingsTableEDFFOn}>
            <div className={AnalyticsExtensionsSettingsStyles.connectionRowEDFFOn}>
              <div className={AnalyticsExtensionsSettingsStyles.connectionHeaderCellEDFFOn} style={{width: '10%'}}>
              </div>
              <div className={AnalyticsExtensionsSettingsStyles.connectionHeaderCellEDFFOn}
                   style={{width: '40%', verticalAlign: 'middle'}}>
                <InputLabelWidget testId="anext_connection_type_title_ed_on" style={{fontWeight: 'bold'}}>
                  {Messages.analyticsExtensionsSettingsConnectionType()}
                </InputLabelWidget>
              </div>
              <div className={AnalyticsExtensionsSettingsStyles.connectionHeaderCellEDFFOn}
                   style={{width: '50%', verticalAlign: 'middle'}}>
                <InputLabelWidget testId="anext_hostname_title_ed_on" style={{fontWeight: 'bold'}}>
                  {Messages.analyticsExtensionsSettingsHostname()}
                </InputLabelWidget>
              </div>
            </div>
          </div>
          <div
            className={AnalyticsExtensionsSettingsStyles.connectionSettingsTableEDFFOn}>
            <div
              className={this.props.values.connectionType === ConnectionTypes.SELECT ? AnalyticsExtensionsSettingsStyles.connectionRow : AnalyticsExtensionsSettingsStyles.Hide}>
              <div className={AnalyticsExtensionsSettingsStyles.noConnectionIcon}
                   style={{backgroundSize: 'contain', backgroundRepeat: 'no-repeat'}}></div>
              <div className={AnalyticsExtensionsSettingsStyles.noConnectionTextLabel} style={{textAlign: 'center'}}>
                <InputLabelWidget testId="anext_no_connection_label_ed_on"
                                  className={AnalyticsExtensionsSettingsStyles.noConnectionText} style={{fontSize: 18}}>
                  {Messages.analyticsExtensionsNoConnectionConfiguredEDFFOn()}
                </InputLabelWidget>
              </div>
              <div className={AnalyticsExtensionsSettingsStyles.createNewConnectionButton}>
                <ButtonWidget
                  testId="anext_connection_create_button_ed_on"
                  buttonType={ButtonType.Outline}
                  handleClick={() => {
                    if (FeatureFlags.analyticsExtensionsSettingsHybridDialog) {
                      this.showHybridDialog(this.props, this.props.values.connectionLUID).then(
                        (result: IAnalyticsExtConnectionSettings) => {
                          this.updateConnectionInfo(result);
                        }
                      );
                    } else {
                      this.showConnectionDialog(this.props, this.props.values.connectionLUID).then(
                        (result: IAnalyticsExtConnectionSettings) => {
                          this.updateConnectionInfo(result);
                        }
                      );
                    }
                  }}>{Messages.analyticsExtensionsSettingsCreateConnectionEDFFOn()}</ButtonWidget>
              </div>
            </div>
            <div
              className={this.props.values.connectionType === ConnectionTypes.SELECT ? AnalyticsExtensionsSettingsStyles.Hide : AnalyticsExtensionsSettingsStyles.connectionRowEDFFOn}>
              <div className={AnalyticsExtensionsSettingsStyles.connectionCellIconEDFFOn}
                   style={{
                     width: '10%',
                     backgroundImage: this.getConnectionTypeIcon(this.props.values.connectionType)
                   }}>
              </div>
              <div className={AnalyticsExtensionsSettingsStyles.connectionCellEDFFOn} style={{width: '40%'}}>
                <InputLabelWidget testId="anext_connection_type_value_ed_on" style={{color: '#1A699E'}}>
                  {this.props.values.connectionType}
                </InputLabelWidget>
              </div>
              <div className={AnalyticsExtensionsSettingsStyles.connectionCellEDFFOn} style={{width: '40%'}}>
                <InputLabelWidget testId="anext_hostname_value_ed_on">
                  {this.props.values.hostName}
                </InputLabelWidget>
              </div>
              <div className={AnalyticsExtensionsSettingsStyles.connectionCellEDFFOn} style={{width: '5%'}}>
                <ButtonWidget
                  testId="anext_connection_edit_button_ed_on"
                  buttonType={ButtonType.Icon}
                  id="editConnectionBtn"
                  className={AnalyticsExtensionsSettingsStyles.editConnectionButton}
                  handleClick={() => {
                    if (FeatureFlags.analyticsExtensionsSettingsHybridDialog) {
                      this.showHybridDialog(this.props, this.props.values.connectionLUID).then(
                        (result: IAnalyticsExtConnectionSettings) => {
                          this.updateConnectionInfo(result);
                        }
                      );
                    } else {
                      this.showConnectionDialog(this.props, this.props.values.connectionLUID).then(
                        (result: IAnalyticsExtConnectionSettings) => {
                          this.updateConnectionInfo(result);
                        }
                      );
                    }
                  }}>
                </ButtonWidget>
              </div>
              <div className={AnalyticsExtensionsSettingsStyles.connectionCellEDFFOn}
                   style={{width: '5%', verticalAlign: 'middle'}}>
                <ButtonWidget
                  testId="anext_connection_delete_button_ed_on"
                  buttonType={ButtonType.Icon}
                  id="deleteConnectionBtn"
                  className={AnalyticsExtensionsSettingsStyles.deleteConnectionButton}
                  onClick={() => this.sendDelete(this.props.values.connectionLUID)}>
                </ButtonWidget>
              </div>
            </div>
          </div>
        </div>;
    } else if (FeatureFlags.analyticsExtensionsNamedConnections) {
      let extensionRows = [];

      for (let namedConnection of this.props.values.namedConnectionList) {
        extensionRows.push(
          <div
            className={AnalyticsExtensionsSettingsStyles.connectionSettingsTableNCFFOn}>
            <div
              className={namedConnection.connection_brief.connection_type === ConnectionTypes.SELECT ? AnalyticsExtensionsSettingsStyles.connectionRow : AnalyticsExtensionsSettingsStyles.Hide}>
              <div className={AnalyticsExtensionsSettingsStyles.noConnectionIcon}
                   style={{backgroundSize: 'contain', backgroundRepeat: 'no-repeat'}}>
              </div>
              <div className={AnalyticsExtensionsSettingsStyles.noConnectionTextLabel} style={{textAlign: 'center'}}>
                <InputLabelWidget testId="anext_no_connection_label_nc_on"
                                  className={AnalyticsExtensionsSettingsStyles.noConnectionText}
                                  style={{fontSize: 18}}>
                  {Messages.analyticsExtensionsNoConnectionConfiguredEDFFOn()}
                </InputLabelWidget>
              </div>
            </div>
            <div
              className={namedConnection.connection_brief.connection_type === ConnectionTypes.SELECT ? AnalyticsExtensionsSettingsStyles.Hide : AnalyticsExtensionsSettingsStyles.connectionRowNCFFOn}>
              <div className={AnalyticsExtensionsSettingsStyles.connectionCellIconNCFFOn}
                   style={{width: '10%', backgroundImage: this.getConnectionTypeIcon(namedConnection.connection_brief.connection_type)}}>
              </div>
              <div className={AnalyticsExtensionsSettingsStyles.connectionCellNCFFOn} style={{width: '40%'}}>
                <InputLabelWidget testId="anext_connection_name_value_nc_on">
                  {namedConnection.connection_brief.connection_name}
                </InputLabelWidget>
              </div>
              <div className={AnalyticsExtensionsSettingsStyles.connectionCellNCFFOn} style={{width: '40%'}}>
                <InputLabelWidget testId="anext_connection_type_value_nc_on" style={{color: '#1A699E'}}>
                  {namedConnection.connection_brief.connection_type}
                </InputLabelWidget>
              </div>
              <div className={AnalyticsExtensionsSettingsStyles.connectionCellNCFFOn} style={{width: '5%'}}>
                <ButtonWidget
                  testId={`anext_connection_delete_button_nc_on + ${namedConnection.connection_luid}`}
                  buttonType={ButtonType.Icon}
                  id={`editConnectionButton + ${namedConnection.connection_luid}`}
                  className={AnalyticsExtensionsSettingsStyles.editConnectionButton}
                  handleClick={() => {
                    if (FeatureFlags.analyticsExtensionsSettingsHybridDialog) {
                      this.showHybridDialog(this.props, namedConnection.connection_luid).then(
                        (result: IAnalyticsExtConnectionSettings) => {
                          this.updateConnectionInfo(result);
                        }
                      );
                    } else {
                      this.showConnectionDialog(this.props, namedConnection.connection_luid).then(
                        (result: IAnalyticsExtConnectionSettings) => {
                          this.updateConnectionInfo(result);
                        }
                      );
                    }
                  }}>
                </ButtonWidget>
              </div>
              <div className={AnalyticsExtensionsSettingsStyles.connectionCellNCFFOn}
                   style={{width: '5%', verticalAlign: 'middle'}}>
                <ButtonWidget
                  testId={`anext_connection_delete_button_nc_on + ${namedConnection.connection_luid}`}
                  buttonType={ButtonType.Icon}
                  id={`deleteConnectionButton + ${namedConnection.connection_luid}`}
                  className={AnalyticsExtensionsSettingsStyles.deleteConnectionButton}
                  onClick={() => this.sendDelete(namedConnection.connection_luid)}>
                </ButtonWidget>
              </div>
            </div>
          </div>
        );
      }
      extensionsDisplay =
        <div>
          <div
            className={AnalyticsExtensionsSettingsStyles.connectionSettingsTableNCFFOn}>
            <div className={AnalyticsExtensionsSettingsStyles.connectionRowNCFFOn}>
              <div className={AnalyticsExtensionsSettingsStyles.connectionHeaderCellNCFFOn} style={{width: '10%'}}>
              </div>
              <div className={AnalyticsExtensionsSettingsStyles.connectionHeaderCellNCFFOn}
                   style={{width: '40%', verticalAlign: 'middle'}}>
                <InputLabelWidget testId="anext_connection_name_title_nc_on" style={{fontWeight: 'bold'}}>
                  {Messages.analyticsExtensionsSettingsConnectionName()}
                </InputLabelWidget>
              </div>
              <div className={AnalyticsExtensionsSettingsStyles.connectionHeaderCellNCFFOn}
                   style={{width: '50%', verticalAlign: 'middle'}}>
                <InputLabelWidget testId="anext_connection_type_title_nc_on" style={{fontWeight: 'bold'}}>
                  {Messages.analyticsExtensionsSettingsConnectionType()}
                </InputLabelWidget>
              </div>
            </div>
          </div>
          {extensionRows}
        </div>;
    }


    return (
      <section>
        <div
          className={FeatureFlags.analyticsExtensionsNamedConnections ? AnalyticsExtensionsSettingsStyles.enableForSiteNamedConnectionsOn : AnalyticsExtensionsSettingsStyles.enableForSite}>
          <CheckBoxWithLabelWidget
            checked={this.props.values.enableExternalServicesCheckbox}
            handleChange={(isBoxChecked: boolean) => {
              this.handleChangeAdapter(isBoxChecked, 'enableExternalServicesCheckbox');
            }}
            testId="anext_site_enable_checkbox"
            label={Messages.analyticsExtensionsSettingsSite_checkbox()}
            disabled={!this.props.values.showSiteCheckbox}
          />
          <div
            className={(FeatureFlags.analyticsExtensionsNamedConnections) ? null : AnalyticsExtensionsSettingsStyles.Hide}>
            <ButtonWidget
              testId="anext_create_new_connection_button"
              buttonType={ButtonType.Primary}
              id="addNewConnectionBtn"
              handleClick={() => {
                if (FeatureFlags.analyticsExtensionsSettingsHybridDialog) {
                  this.showHybridDialog(this.props, '').then(
                    (result: IAnalyticsExtConnectionSettings) => {
                      this.updateConnectionInfo(result);
                    }
                  );
                } else {
                  this.showConnectionDialog(this.props, '').then(
                    (result: IAnalyticsExtConnectionSettings) => {
                      this.updateConnectionInfo(result);
                    }
                  );
                }
              }}>{Messages.analyticsExtensionsSettingsCreateConnection()}</ButtonWidget>
          </div>
        </div>
        <div>
          {extensionsDisplay}
        </div>
      </section>
    );
  }
}

export let defaultConnection: IAnalyticsExtConnectionSettings = {
  connection_brief: {
    connection_type: 'SELECT',
    connection_name: ''
  },
  connection_luid: '',
  host: '',
  port: undefined,
  is_ssl_enabled: false,
  is_auth_enabled: false,
  username: '',
  password: ''
};

function getConnectionTypeDisplay(initInfoConnection: IAnalyticsExtConnectionSettings) {
  let connection = ConnectionTypes.SELECT;
  if (initInfoConnection.connection_brief.connection_type === ConnectionTypes.TABPY.TAG) {
    connection = ConnectionTypes.TABPY.DISPLAY;
  }
  if (initInfoConnection.connection_brief.connection_type === ConnectionTypes.RSERVE.TAG) {
    connection = ConnectionTypes.RSERVE.DISPLAY;
  }
  if (initInfoConnection.connection_brief.connection_type === ConnectionTypes.EINSTEIN.TAG) {
    connection = ConnectionTypes.EINSTEIN.DISPLAY;
  }
  if (initInfoConnection.connection_brief.connection_type === ConnectionTypes.GENERIC_API.TAG) {
    connection = ConnectionTypes.GENERIC_API.DISPLAY;
  }
  return connection;
}

export const AnalyticsExtensionsSettingsForSite =
  withFormik<AnalyticsExtensionsSettingsCtrlProps, AnalyticsExtensionsSettingsForSiteValues>({
    enableReinitialize: true,   // Reset the form when initial props change
    validateOnBlur: true,
    mapPropsToValues: props => {
      let initInfoSite = false;
      let initInfoConnection = defaultConnection;
      let connectionType = ConnectionTypes.SELECT;
      let namedConnections: IAnalyticsExtConnectionSettings[] = [];
      if (typeof props.initialAnalyticsExtensionsSettings !== 'undefined') {
        initInfoSite = props.initialAnalyticsExtensionsSettings.enableExtSvcSite;
      }

      // If named connections feature flag is off, we use connectionDefault
      if (!FeatureFlags.analyticsExtensionsNamedConnections) {
        if (props.initialAnalyticsExtensionsSettings?.connectionDefault) {
          initInfoConnection = props.initialAnalyticsExtensionsSettings.connectionDefault;
        }
        connectionType = getConnectionTypeDisplay(initInfoConnection);
      } else if (FeatureFlags.analyticsExtensionsNamedConnections) {
        //If named connections feature flag is on, we map connection list to a set of connection configurations
        if (!props.initialAnalyticsExtensionsSettings || props.initialAnalyticsExtensionsSettings?.connectionSettingsList.length === 0) {
          let connectionBrief = {... defaultConnection.connection_brief, connection_type: connectionType};
          namedConnections.push({...defaultConnection, connection_brief: connectionBrief});
        } else {
          for (let connection of props.initialAnalyticsExtensionsSettings?.connectionSettingsList) {
            connectionType = getConnectionTypeDisplay(connection);
            namedConnections.push({
              connection_brief: {
                connection_name: connection.connection_brief.connection_name,
                connection_type: connectionType,
              },
              connection_luid: connection.connection_luid,
              host: connection.host,
              port: connection.port,
              is_ssl_enabled: connection.is_ssl_enabled,
              is_auth_enabled: connection.is_auth_enabled,
              username: connection.username,
              password: connection.password,
            });
          }
        }
      }

      return {
        showAnalyticsSiteSetting: props.displaySettings,
        showSiteCheckbox: props.displaySiteSettingCheckbox,
        enableExternalServicesCheckbox: initInfoSite,
        connectionLUID: initInfoConnection.connection_luid,
        connectionName: initInfoConnection.connection_brief.connection_name,
        connectionType: connectionType,
        hostName: initInfoConnection.host,
        port: initInfoConnection.port,
        isSecure: initInfoConnection.is_ssl_enabled,
        isAuth: initInfoConnection.is_auth_enabled,
        userName: initInfoConnection.username,
        password: initInfoConnection.password,
        namedConnectionList: namedConnections
      };
    },
    validate: (values, props) => {
      const errors: any = {};
      const invalid = 'Invalid';
      if (!FeatureFlags.anExtRedesignV20203Enabled) {
        let connectionTypeSelected = (values.connectionType) && (values.connectionType !== ConnectionTypes.SELECT);
        // If there wasn't a connection previously, and no connection created just now, there is no validation error
        if ((!connectionTypeSelected) && (!props.initialAnalyticsExtensionsSettings?.connectionDefault)) {
          return errors;
        } else {
          if ((!connectionTypeSelected) && (props.initialAnalyticsExtensionsSettings?.connectionDefault)) {
            errors.connectionType = invalid;
          }
          if ((!values.hostName) || (values.hostName.length > MAX_HOSTNAME_LENGTH)) {
            errors.hostName = invalid;
          }
          if ((!values.port) || (!Number.isInteger(+values.port)) || (+values.port < MIN_PORT_NUMBER) || (+values.port > MAX_PORT_NUMBER)) {
            errors.port = invalid;
          }
          if (values.isAuth && values.userName && (values.userName.length > MAX_USERNAME_LENGTH)) {
            errors.userName = invalid;
          }
          if (values.isAuth && values.password && (values.password.length > MAX_PASSWORD_LENGTH)) {
            errors.password = invalid;
          }
        }
      }
      return errors;
    },
    handleSubmit: (values, actions) => {
      // Reset the form after submission happens
      actions.setSubmitting(false);
      actions.resetForm({values});
    },
  })(InnerAnalyticsExtensionsSettingForSite);
